"use client";

import * as React from "react";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useRewardPools } from "../../queries/use-reward-pools";
import { useUserDeposits } from "../../queries/use-user-deposits";
import { useTokenInfo } from "../../queries/use-token-info";
import { useWithdrawPositions } from "../../queries/use-withdraw-positions";
import { APP_LAUNCHER_ICONS_BASE, REWARDS_WEB } from "../../constants";
import type { RewardPool } from "../../types";
import { formatPortfolioTokenAmount, formatPortfolioUsd } from "../../utils";
import { SectionCard } from "./SectionCard";
import { WithdrawDialog, type WithdrawDialogPosition } from "./WithdrawDialog";
import { useWithdrawFlow } from "./useWithdrawFlow";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";
import BigNumber from "bignumber.js";

const SECTION_TITLE = "CROSS Rewards";

function isPositiveRawAmount(raw: string): boolean {
  return new BigNumber(raw || "0").isGreaterThan(0);
}

export function RewardsSection() {
  const { env, walletAddress, onOutlink } = useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { allPools, isLoading: poolsLoading } = useRewardPools(env, hasAddress);
  const { depositMap, isLoading: depositsLoading } = useUserDeposits(
    env,
    walletAddress,
    hasAddress,
  );
  const { getTokenImage } = useTokenInfo(env, hasAddress);
  const flow = useWithdrawFlow("rewards");
  const { positions } = useWithdrawPositions(
    env,
    walletAddress,
    hasAddress && flow.canWithdrawInline,
  );

  // 이 섹션의 다이얼로그 토큰 리스트: CROSS 예치(CrossPool) 포지션.
  // 게임 토큰 예치는 Points 섹션 소관.
  const dialogPositions = React.useMemo<WithdrawDialogPosition[]>(
    () =>
      positions
        .filter((p) => p.poolType === "CrossPool")
        .map((p) => ({
          ...p,
          icon: getTokenImage(p.tokenAddress, p.tokenSymbol),
        })),
    [positions, getTokenImage],
  );

  const getRewardsUrl = (pool: RewardPool, action: "withdraw" | "claim") =>
    `${REWARDS_WEB[env]}/rewards/${pool.pool_address}?action=${action}`;

  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/reward.png`;

  const visiblePools = allPools.filter((pool) => {
    const userDeposit = depositMap.get(pool.pool_address.toLowerCase());
    const deposited = userDeposit?.deposited_amount ?? "0";
    const claimable = userDeposit?.claimable_reward ?? "0";
    return !(new BigNumber(deposited).isZero() && new BigNumber(claimable).isZero());
  });

  return (
    <>
      <SectionCard
        sectionKey="rewards"
        title={SECTION_TITLE}
        icon={sectionIcon}
        isEmpty={visiblePools.length === 0}
        loading={poolsLoading || depositsLoading}
      >
        {visiblePools.map((pool) => {
          const userDeposit = depositMap.get(pool.pool_address.toLowerCase());
          const deposited = userDeposit?.deposited_amount ?? "0";
          const rewardRaw = userDeposit?.claimable_reward ?? "0";
          const withdrawUrl = getRewardsUrl(pool, "withdraw");
          const claimUrl = getRewardsUrl(pool, "claim");
          const depositIcon = getTokenImage(
            pool.deposit_token.address,
            pool.deposit_token.symbol,
          );
          const firstRewardToken = pool.reward_tokens[0];
          const firstRewardIcon = firstRewardToken
            ? getTokenImage(firstRewardToken.address, firstRewardToken.symbol)
            : "";
          const rewardSymbols = pool.reward_tokens.map((rt) => rt.symbol);
          const poolLabel = rewardSymbols.length > 0
            ? `${pool.deposit_token.symbol}-${rewardSymbols.join("/")}`
            : pool.deposit_token.symbol;
          const rewardAmount = firstRewardToken
            ? formatPortfolioTokenAmount(rewardRaw, firstRewardToken.decimals)
            : "0";
          const rewardSymbol = firstRewardToken?.symbol ?? "Reward";
          const depositedUsd = formatPortfolioUsd(
            deposited,
            pool.deposit_token.decimals,
            pool.deposit_token.price,
            fiat,
          );
          const rewardUsd = firstRewardToken
            ? formatPortfolioUsd(
                rewardRaw,
                firstRewardToken.decimals,
                firstRewardToken.price,
                fiat,
              )
            : null;
          const canClaim = !!userDeposit && isPositiveRawAmount(rewardRaw);
          const canWithdraw = !!userDeposit && isPositiveRawAmount(deposited);
          const inlineWithdrawReady =
            flow.canWithdrawInline &&
            dialogPositions.some(
              (p) =>
                p.poolAddress.toLowerCase() ===
                pool.pool_address.toLowerCase(),
            );

          return (
            <article
              className="wp-item"
              data-side="reward"
              key={pool.pool_address}
            >
              <div className="wp-item-body">
                <div className="wp-item-line">
                  <span className="wp-item-label">Pool (Deposit - Earn)</span>
                  <div className="wp-svc-token">
                    <span className="wp-token-stack" aria-hidden>
                      {depositIcon && (
                        <img
                          className="wp-svc-token-icon"
                          src={depositIcon}
                          alt=""
                        />
                      )}
                      {firstRewardIcon && (
                        <img
                          className="wp-svc-token-icon"
                          src={firstRewardIcon}
                          alt=""
                        />
                      )}
                    </span>
                    <span>{poolLabel}</span>
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">Deposited</span>
                  <div className="wp-svc-token wp-svc-token--amount">
                    {depositIcon && (
                      <img
                        className="wp-svc-token-icon"
                        src={depositIcon}
                        alt=""
                      />
                    )}
                    <div className="wp-svc-amount">
                      <span className="wp-svc-amount-main">
                        {formatPortfolioTokenAmount(
                          deposited,
                          pool.deposit_token.decimals,
                        )}
                      </span>
                      {depositedUsd && (
                        <span className="wp-item-usd">{depositedUsd}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">
                    Claimable ({rewardSymbol})
                  </span>
                  <div className="wp-svc-token wp-svc-token--amount">
                    {firstRewardIcon && (
                      <img
                        className="wp-svc-token-icon"
                        src={firstRewardIcon}
                        alt=""
                      />
                    )}
                    <div className="wp-svc-amount">
                      <span className="wp-svc-amount-main">{rewardAmount}</span>
                      {rewardUsd && (
                        <span className="wp-item-usd">{rewardUsd}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="wp-item-tags">
                {inlineWithdrawReady ? (
                  <button
                    type="button"
                    className="wp-tag wp-tag--muted"
                    data-disabled={!canWithdraw || undefined}
                    aria-disabled={!canWithdraw || undefined}
                    data-track="rewards_withdraw_open"
                    onClick={() => {
                      if (!canWithdraw) return;
                      flow.openWithdraw(pool.pool_address);
                    }}
                  >
                    <span className="wp-tag-label">Withdraw</span>
                  </button>
                ) : (
                  <a
                    className="wp-tag wp-tag--muted"
                    href={withdrawUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-track="rewards_withdraw_open"
                    onClick={(e) =>
                      handleOutlinkClick(
                        e,
                        withdrawUrl,
                        {
                          category: "portfolio",
                          origin: "portfolio-rewards",
                          payload: { pool, userDeposit },
                        },
                        onOutlink,
                      )
                    }
                  >
                    <span className="wp-tag-label">Withdraw</span>
                    <ExternalLinkIcon size={12} className="wp-tag-icon" />
                  </a>
                )}
                <a
                  className="wp-tag wp-tag--mint"
                  data-disabled={!canClaim || undefined}
                  aria-disabled={!canClaim || undefined}
                  href={canClaim ? claimUrl : undefined}
                  target={canClaim ? "_blank" : undefined}
                  rel={canClaim ? "noopener noreferrer" : undefined}
                  onClick={(e) => {
                    if (!canClaim) {
                      e.preventDefault();
                      return;
                    }
                    handleOutlinkClick(
                      e,
                      claimUrl,
                      {
                        category: "portfolio",
                        origin: "portfolio-rewards",
                        payload: { pool, userDeposit },
                      },
                      onOutlink,
                    );
                  }}
                >
                  <span className="wp-tag-label">Claim</span>
                  <ExternalLinkIcon size={12} className="wp-tag-icon" />
                </a>
              </div>
            </article>
          );
        })}
      </SectionCard>

      <WithdrawDialog
        open={flow.isOpen}
        status={flow.status}
        positions={dialogPositions}
        initialPoolAddress={flow.initialPoolAddress}
        walletAddress={walletAddress}
        txHash={flow.txHash}
        error={flow.error}
        copied={flow.copied}
        estimateWithdraw={flow.hasEstimator ? flow.estimateWithdraw : undefined}
        onClose={flow.closeWithdraw}
        onConfirm={flow.confirmWithdraw}
        onCopyHash={flow.copyHash}
        trackFunnel={flow.trackFunnelOnce}
      />
    </>
  );
}
